import type { Automation, AutomationRun, AutomationStep, UpsertAutomation } from '@dnx/contracts';
import { queryOptions } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { STAGE_LABELS } from '../lib/labels.js';

export const automationsQuery = queryOptions({ queryKey: ['automations'], queryFn: () => api<Automation[]>('/automations') });
export const runsQuery = (id: string) => queryOptions({ queryKey: ['automations', id, 'runs'], queryFn: () => api<AutomationRun[]>(`/automations/${id}/runs`) });

export const TRIGGER_LABEL = { lead_created: 'Lead novo chegou', lead_stage_changed: 'Lead mudou de etapa', tag_added: 'Etiqueta aplicada' } as const;
export const MOVE_STAGES = ['new', 'qualifying', 'evaluation_scheduled', 'evaluated', 'quoted', 'won'] as const;

/** Tempo legível: 90 → "1 h 30 min", 2880 → "2 dias". */
export function humanWait(minutes: number): string {
  if (minutes % 1440 === 0) return `${minutes / 1440} ${minutes === 1440 ? 'dia' : 'dias'}`;
  if (minutes % 60 === 0) return `${minutes / 60} h`;
  return minutes > 60 ? `${Math.floor(minutes / 60)} h ${minutes % 60} min` : `${minutes} min`;
}

export const triggerSummary = (a: Pick<Automation, 'trigger' | 'triggerValue'>, tagName: (id: string) => string) =>
  a.trigger === 'lead_stage_changed' ? `Lead foi para “${STAGE_LABELS[a.triggerValue ?? ''] ?? a.triggerValue}”`
    : a.trigger === 'tag_added' ? `Etiqueta “${tagName(a.triggerValue ?? '')}” aplicada` : TRIGGER_LABEL.lead_created;

export const EMPTY: UpsertAutomation = {
  name: '', enabled: true, trigger: 'lead_created', triggerValue: null, conditions: {}, stopOnReply: true,
  steps: [{ type: 'send_message', text: '' }],
};

const msg = (text: string): AutomationStep => ({ type: 'send_message', text });
const wait = (days: number): AutomationStep => ({ type: 'wait', minutes: days * 1440 });

/** Modelos para começar (a clínica ajusta os textos). */
export const TEMPLATES: { title: string; description: string; value: UpsertAutomation }[] = [
  {
    title: 'Follow-up de lead novo',
    description: 'Se a pessoa parar de responder, duas retomadas: depois de 1 dia e depois de mais 3.',
    value: {
      ...EMPTY, name: 'Follow-up de lead novo',
      steps: [
        wait(1),
        msg('{nome}, passando para saber se ficou alguma dúvida. A avaliação é o primeiro passo e é sem compromisso. 😊'),
        wait(3),
        msg('Oi, {nome}! Última mensagem por aqui: quando quiser conversar sobre o seu caso, é só responder. Estamos à disposição.'),
      ],
    },
  },
  {
    title: 'Depois do orçamento',
    description: 'Dois dias depois do orçamento, a clínica retoma o contato.',
    value: {
      ...EMPTY, name: 'Retomada pós-orçamento', trigger: 'lead_stage_changed', triggerValue: 'quoted',
      steps: [wait(2), msg('Oi, {nome}! Conseguiu avaliar o orçamento? Se quiser, {atendente} explica as formas de pagamento e tira qualquer dúvida.')],
    },
  },
  {
    title: 'Avaliação agendada',
    description: 'Mensagem de boas-vindas com orientações assim que a avaliação é marcada.',
    value: {
      ...EMPTY, name: 'Boas-vindas da avaliação', trigger: 'lead_stage_changed', triggerValue: 'evaluation_scheduled', stopOnReply: false,
      steps: [msg('{nome}, que bom que você vem conhecer a {clinica}! No dia, chegue 10 minutos antes e traga um documento com foto. Qualquer dúvida, é só chamar aqui.')],
    },
  },
];
